"use client";

import { Button } from "@/components/builder/button";
import { Typography } from "@/components/builder/typography";
import Cross from "@/components/icons/cross-icon";
import Tick from "@/components/icons/tick-icon";
import { WarningIcon } from "@/components/icons/warning-icon";
import { env } from "@/env.mjs";
import { useLocale, useTranslations } from "@/libs/base/components/locale-provider";
import { MimeTypes } from "@/libs/base/constants/mime-types";
import { cn } from "@/libs/base/utils/layout";
import { contactMessageSchema } from "@/models/contact";
import { Turnstile } from "@marsidev/react-turnstile";
import { FieldApi, useForm } from "@tanstack/react-form";
import React from "react";
import { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes, forwardRef, useState } from "react";

type FormState = {
  isSuccessful: boolean;
  isSubmitted: boolean;
  hasError: boolean;
};

export default function ContactForm() {
  const locale = useLocale();
  const { contact_form: contactFormTranslations } = useTranslations();
  const formRef = React.useRef<HTMLFormElement>(null);

  const [formState, setFormState] = useState<FormState>({
    isSuccessful: false,
    isSubmitted: false,
    hasError: false,
  });

  const form = useForm({
    defaultValues: {
      language: locale,
      fullName: "",
      email: "",
      phoneNumber: "",
      message: "",
    },
    validators: {
      onChange: contactMessageSchema,
    },
    onSubmit: async ({ value }) => {
      // FIXME: missing CSRF
      const response = await fetch("/api/contact/send", {
        method: "POST",
        headers: {
          "Content-Type": MimeTypes.APPLICATION_JSON,
        },
        body: JSON.stringify({
          ...value,
          cfTurnstileToken: new FormData(formRef.current!).get("cf-turnstile-response"),
        }),
      });

      if (!response.ok) {
        setFormState({ ...formState, hasError: true, isSubmitted: true });
        return;
      }

      setFormState({ ...formState, isSuccessful: true, isSubmitted: true });
      form.reset();
    },
  });

  if (formState.isSubmitted) {
    if (formState.isSuccessful) {
      return (
        <AfterSubmitBox
          message={contactFormTranslations.success.message}
          height={formRef.current?.offsetHeight}
          icon={<Tick width={73} height={51} />}
        />
      );
    }

    return (
      <AfterSubmitBox
        message={contactFormTranslations.failure.header}
        height={formRef.current?.offsetHeight}
        icon={<Cross width={51} height={51} />}
      />
    );
  }

  return (
    <form
      ref={formRef}
      className="grid grid-cols-1 grid-rows-[min-content,1fr,1fr,1fr,3fr,1fr] gap-x-24 gap-y-4 lg:grid-cols-2 lg:grid-rows-[min-content,1fr,1fr,1fr,1fr] lg:gap-y-9"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Typography size="Header" className="col-start-1 col-end-1 pb-4 lg:pb-0" text={contactFormTranslations.header} />

      <form.Field
        name="fullName"
        children={(field) => (
          <Input
            className="col-start-1 col-end-1"
            aria-label={contactFormTranslations.full_name}
            placeholder={contactFormTranslations.full_name}
            field={field}
          />
        )}
      />

      <form.Field
        name="email"
        children={(field) => (
          <Input
            className="col-start-1 col-end-1"
            aria-label={contactFormTranslations.email}
            placeholder={contactFormTranslations.email}
            field={field}
          />
        )}
      />

      <form.Field
        name="phoneNumber"
        children={(field) => (
          <Input
            className="col-start-1 col-end-1"
            aria-label={contactFormTranslations.phone_number}
            placeholder={contactFormTranslations.phone_number}
            field={field}
          />
        )}
      />

      <form.Field
        name="message"
        children={(field) => (
          <TextArea
            className="lg:col-start-2 lg:col-end-2 lg:row-start-2 lg:row-end-5"
            aria-label={contactFormTranslations.message}
            placeholder={contactFormTranslations.message}
            field={field}
          />
        )}
      />

      <form.Subscribe
        selector={(state) => [state.canSubmit, state.isSubmitting]}
        children={([canSubmit, isSubmitting]) => (
          <Button
            disabled={!canSubmit}
            type="submit"
            text={isSubmitting ? "..." : contactFormTranslations.submit}
            className="text-body lg:col-start-2 lg:col-end-2 lg:row-start-5 lg:row-end-5"
          />
        )}
      />
      <Turnstile siteKey={env.NEXT_PUBLIC_TURNSTILE_SITE_KEY} />
    </form>
  );
}

const AfterSubmitBox = ({
  message,
  height,
  icon,
}: {
  message: string;
  height: number | undefined;
  icon: React.JSX.Element;
}) => {
  return (
    // Fallback height in case the passed one from the form ref is undefined. In theory it shouldn't happen...
    <div className="grid lg:grid-cols-2" style={{ minHeight: height || "350px" }}>
      <div>
        <Typography text={message} size="Header"></Typography>
      </div>

      <div className="relative">
        <div className="absolute bottom-1 right-1">{icon}</div>
      </div>
    </div>
  );
};

const fieldHasIssue = (field: FieldApi<any, any, any, any>) =>
  field.state.meta.isTouched && field.state.meta.errors.length > 0;

const Field = ({
  className,
  field,
  children,
}: {
  className?: string;
  field: FieldApi<any, any, any, any>;
  children: ReactNode;
}) => {
  const [errorsOpen, setErrorsOpen] = useState(false);

  const hasIssue = fieldHasIssue(field);

  return (
    <div className={cn("relative", className)}>
      {children}

      {hasIssue && (
        <button
          type="submit"
          className="absolute bottom-0 right-2 top-0 h-fit"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setErrorsOpen((open) => !open);
          }}
        >
          <div className="relative">
            <div>
              <WarningIcon className="text-red-500" />
            </div>

            {errorsOpen && (
              <div className="absolute right-0 top-0 z-20 w-48 space-y-1 border border-red-500 bg-white px-4 py-3">
                {fieldHasIssue(field) ? <p className="text-body">{field.state.meta.errors.join(", ")}</p> : null}
                <p className="text-minimum text-lightgray-300">(click to dismiss)</p>
              </div>
            )}
          </div>
        </button>
      )}
    </div>
  );
};

const Input = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, "name" | "id" | "value" | "onBlur" | "onChange"> & {
    field: FieldApi<any, any, any, any>;
  }
>(({ className, field, ...props }, ref) => (
  <Field className={className} field={field}>
    <input
      ref={ref}
      id={field.name}
      name={field.name}
      value={field.state.value}
      onBlur={field.handleBlur}
      onChange={(e) => field.handleChange(e.target.value)}
      className={cn(
        "h-full w-full border-x-0 border-t-0 px-0 pb-1.5 outline-none transition-all placeholder:text-lightgray-300 focus:outline-none focus:ring-0",
        fieldHasIssue(field)
          ? "border-b-2 border-b-red-500 focus:border-b-red-500"
          : "border-b-1 border-b-black focus:border-b-black",
      )}
      {...props}
    />
  </Field>
));

const TextArea = forwardRef<
  HTMLTextAreaElement,
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "name" | "id" | "value" | "onBlur" | "onChange"> & {
    field: FieldApi<any, any, any, any>;
  }
>(({ className, field, ...props }, ref) => (
  <Field className={className} field={field}>
    <textarea
      ref={ref}
      id={field.name}
      name={field.name}
      value={field.state.value}
      onBlur={field.handleBlur}
      onChange={(e) => field.handleChange(e.target.value)}
      className={cn(
        "border-b-1 h-full w-full border-x-0 border-t-0 px-0 pb-1.5 outline-none placeholder:text-lightgray-300 focus:outline-none focus:ring-0",
        fieldHasIssue(field) ? "border-b-red-500 focus:border-b-red-500" : "border-b-black focus:border-b-black",
      )}
      {...props}
    />
  </Field>
));
